import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import fileFinityLogo from '@/assets/filefinity-logo.png';
import onefinityLogo from '@/assets/onefinity_logo.png';

interface CarouselHeaderProps {
  disabled: boolean;
  title?: string;
  description?: string;
  showSaveChanges?: boolean;
  showNext?: boolean;
  showCustomAction?: boolean;
  customActionLabel?: string;
  showClose?: boolean;
  onSaveChanges?: () => void;
  onNext?: () => void;
  onBack?: () => void;
  onCustomAction?: () => void;
  onClose?: () => void;
  isSaving?: boolean;
  isNextLoading?: boolean;
  isNextDisabled?: boolean;
  showLogo?: boolean;
  nextLabel?: string;
}

function CarouselHeader({
  disabled,
  title,
  description,
  showSaveChanges = false,
  showNext = false,
  showCustomAction = false,
  customActionLabel = 'Action',
  showClose = false,
  onSaveChanges,
  onNext,
  onBack,
  onCustomAction,
  onClose,
  isSaving = false,
  isNextLoading = false,
  isNextDisabled = false,
  showLogo = false,
  nextLabel = 'Next',
}: CarouselHeaderProps) {
  return (
    <div className="w-full bg-white border-b border-gray-200">
      <div className="flex items-center px-3 sm:px-4 py-2 sm:py-3 gap-2">
        {/* Left side: Back button + title */}
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {onBack && (
            <Button
              variant="ghost"
              onClick={onBack}
              disabled={disabled}
              className="p-2 hover:bg-gray-100 flex-shrink-0"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
          )}
          {(title || description) && (
            <div className="hidden sm:flex flex-col min-w-0">
              {title && (
                <h2 className="text-lg font-semibold text-gray-900 truncate">
                  {title}
                </h2>
              )}
              {description && (
                <p className="text-sm text-gray-600 line-clamp-1">
                  {description}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Center: Logos (hidden on mobile) */}
        {showLogo && (
          <div className="hidden sm:flex items-center gap-2 flex-shrink-0">
            <Link to="/" title="Filefinity">
              <img
                src={fileFinityLogo}
                alt="filefinity logo"
                className="h-10 w-auto object-contain"
              />
            </Link>
            <div className="h-6 w-px bg-gray-300 self-center" />
            <a
              href="https://www.onefinitycnc.com/"
              target="_blank"
              rel="noopener noreferrer"
              title="Onefinity"
            >
              <img
                src={onefinityLogo}
                alt="onefinity logo"
                className="h-10 w-auto object-contain"
              />
            </a>
          </div>
        )}

        {/* Right side: Action buttons */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {showSaveChanges && (
            <Button
              variant="outline"
              size="sm"
              onClick={onSaveChanges}
              disabled={disabled || isSaving}
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          )}
          {showCustomAction && (
            <Button
              onClick={onCustomAction}
              size="sm"
              disabled={disabled}
              className="bg-green-600 hover:bg-green-500 text-white"
            >
              {customActionLabel}
            </Button>
          )}
          {showNext && (
            <Button
              onClick={onNext}
              size="sm"
              disabled={disabled || isNextLoading || isNextDisabled}
              className="bg-green-600 hover:bg-green-500 text-white"
            >
              {isNextLoading ? 'Loading...' : nextLabel}
            </Button>
          )}
          {showClose && (
            <Button
              variant="outline"
              size="sm"
              onClick={onClose}
              disabled={disabled}
            >
              Close
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export default CarouselHeader;
